<template>
  <div v-if="show" class="modal-backdrop-custom">
    <div class="modal-card card shadow" style="width: 520px; max-width: 95vw">
      <div
        class="card-header d-flex justify-content-between align-items-center"
      >
        <div>
          <strong>Set Beneficiary Opening Balance</strong>
          <div class="small text-muted mt-1">{{ beneficiarySubtitle }}</div>
        </div>

        <button
          class="btn btn-sm btn-outline-secondary"
          :disabled="saving"
          @click="close"
        >
          X
        </button>
      </div>

      <div class="card-body">
        <div v-if="error" class="alert alert-danger py-2 mb-3">
          {{ error }}
        </div>

        <div class="mb-3">
          <label class="form-label">Beneficiary</label>
          <input
            class="form-control"
            :value="beneficiary?.name || ''"
            disabled
          />
        </div>

        <div class="row g-3">
          <div class="col-md-6">
            <label class="form-label">Guardian</label>
            <input
              class="form-control"
              :value="beneficiary?.guardian_name || '-'"
              disabled
            />
          </div>

          <div class="col-md-6">
            <label class="form-label">As of Period</label>
            <input
              ref="periodInput"
              class="form-control"
              placeholder="Select period"
            />
          </div>
        </div>

        <div class="mt-3">
          <label class="form-label">Amount</label>
          <input
            v-model.number="form.amount"
            type="number"
            min="0"
            step="0.01"
            class="form-control"
            placeholder="Enter opening amount"
            :disabled="saving"
          />
        </div>

        <div class="mt-3">
          <label class="form-label">Note</label>
          <textarea
            v-model.trim="form.note"
            rows="3"
            class="form-control"
            placeholder="Optional note"
            :disabled="saving"
          ></textarea>
        </div>
      </div>

      <div class="card-footer d-flex justify-content-end gap-2">
        <button
          class="btn btn-outline-secondary"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </button>

        <button class="btn btn-primary" :disabled="saving" @click="save">
          {{ saving ? "Saving..." : existingId ? "Update" : "Save" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import flatpickr from "flatpickr";
import monthSelectPlugin from "flatpickr/dist/plugins/monthSelect";
import "flatpickr/dist/flatpickr.css";
import "flatpickr/dist/plugins/monthSelect/style.css";

import {
  createOpeningBalance,
  updateOpeningBalance,
  getOpeningBalanceForParticipant,
} from "@/services/openingBalances";

export default {
  name: "BeneficiaryOpeningBalanceModal",
  emits: ["saved"],

  data() {
    return {
      show: false,
      saving: false,
      error: "",
      beneficiary: null,
      existingId: null,
      periodPicker: null,
      form: {
        as_of_period: "",
        amount: "",
        note: "",
      },
    };
  },

  computed: {
    beneficiarySubtitle() {
      if (!this.beneficiary) return "";
      return `Beneficiary under ${
        this.beneficiary.guardian_name || "guardian"
      }`;
    },
  },

  methods: {
    initDatePicker() {
      if (!this.$refs.periodInput) return;

      if (this.periodPicker) {
        this.periodPicker.destroy();
        this.periodPicker = null;
      }

      this.periodPicker = flatpickr(this.$refs.periodInput, {
        plugins: [
          new monthSelectPlugin({
            shorthand: true,
            dateFormat: "Y-m",
            altFormat: "F Y",
          }),
        ],
        dateFormat: "Y-m",
        defaultDate: this.form.as_of_period || null,
        onChange: (_, dateStr) => {
          this.form.as_of_period = dateStr;
        },
      });
    },

    reset() {
      this.saving = false;
      this.error = "";
      this.beneficiary = null;
      this.existingId = null;

      if (this.periodPicker) {
        this.periodPicker.destroy();
        this.periodPicker = null;
      }

      this.form = {
        as_of_period: "",
        amount: "",
        note: "",
      };
    },

    async open(beneficiary, period = "") {
      this.reset();
      this.show = true;
      this.beneficiary = beneficiary ? { ...beneficiary } : null;
      this.form.as_of_period = period || "";

      if (!this.beneficiary?.id) return;

      try {
        const res = await getOpeningBalanceForParticipant({
          user_id: this.beneficiary.guardian_user_id,
          beneficiary_id: this.beneficiary.id,
        });

        const item = res?.data?.data || res?.data || null;

        if (item) {
          this.existingId = item.id || null;
          this.form.amount = item.amount ?? "";
          this.form.note = item.note ?? "";
          this.form.as_of_period = item.as_of_period || this.form.as_of_period;
        }
      } catch (e) {
        const status = e?.response?.status;
        if (status && status !== 404) {
          this.error =
            e?.response?.data?.message ||
            "Failed to load existing opening balance";
        }
      }

      this.$nextTick(() => {
        this.initDatePicker();
      });
    },

    validate() {
      if (!this.beneficiary?.id) {
        this.error = "Beneficiary is required.";
        return false;
      }

      if (!this.beneficiary?.guardian_user_id) {
        this.error = "Guardian user is required.";
        return false;
      }
      if (!this.form.as_of_period) {
        this.error = "As of period is required.";
        return false;
      }

      if (
        this.form.amount === "" ||
        this.form.amount === null ||
        Number(this.form.amount) < 0
      ) {
        this.error = "Amount must be zero or greater.";
        return false;
      }

      return true;
    },

    buildPayload() {
      return {
        user_id: this.beneficiary.guardian_user_id,
        beneficiary_id: this.beneficiary.id,
        as_of_period: this.form.as_of_period,
        amount: Number(this.form.amount || 0),
        note: this.form.note || null,
      };
    },

    async save() {
      this.error = "";
      if (!this.validate()) return;

      this.saving = true;

      try {
        const payload = this.buildPayload();

        if (this.existingId) {
          await updateOpeningBalance(this.existingId, payload);
        } else {
          await createOpeningBalance(payload);
        }

        this.$emit("saved");
        this.close();
      } catch (e) {
        this.error =
          e?.response?.data?.message ||
          Object.values(e?.response?.data?.errors || {})?.flat()?.[0] ||
          "Failed to save opening balance";
      } finally {
        this.saving = false;
      }
    },
    close() {
      this.show = false;
      this.reset();
    },
  },

  beforeUnmount() {
    if (this.periodPicker) {
      this.periodPicker.destroy();
      this.periodPicker = null;
    }
  },
};
</script>

<style scoped>
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 12px;
}

.modal-card {
  border-radius: 12px;
}
</style>
